import localforage from "localforage";

import { LocalStorageKey } from "@/modules/local-storage/constants";
import {
  LocalStoragePendingRedirectPath,
  LocalStoragePendingRedirectPathInput,
} from "@/modules/local-storage/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

const IGNORED_REDIRECT_PATHS = [
  /** Seems to be a weird redirect from Webflow. */
  "/xdomain.html",
];

const IGNORED_SEARCH_PARAMS = new Set(["tid"]);

const normalizeSegment = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  return value.length > 0 ? value : null;
};

const sanitizeSearch = (value: string | null | undefined): string | null => {
  const normalized = normalizeSegment(value);

  if (!normalized) {
    return null;
  }

  const trimmed = normalized.startsWith("?") ? normalized.slice(1) : normalized;

  if (!trimmed) {
    return null;
  }

  const params = new URLSearchParams(trimmed);

  IGNORED_SEARCH_PARAMS.forEach(param => {
    params.delete(param);
  });

  const sanitized = params.toString();

  if (!sanitized) {
    return null;
  }

  return `?${sanitized}`;
};

export const _writeLocalStoragePendingRedirectPath = async (
  value: LocalStoragePendingRedirectPathInput
): Promise<void> => {
  try {
    const pathname = normalizeSegment(value.pathname);

    if (pathname && IGNORED_REDIRECT_PATHS.includes(pathname)) {
      return;
    }

    const record: LocalStoragePendingRedirectPath = {
      pathname,
      search: sanitizeSearch(value.search),
      hash: normalizeSegment(value.hash),
    };

    await localforage.setItem<LocalStoragePendingRedirectPath>(
      LocalStorageKey.PendingRedirectPath,
      record
    );
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[_writeLocalStoragePendingRedirectPath] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
  }
};

export const _clearLocalStoragePendingRedirectPath = async (): Promise<void> => {
  try {
    await localforage.removeItem(LocalStorageKey.PendingRedirectPath);
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[_clearLocalStoragePendingRedirectPath] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
  }
};

export const _readLocalStoragePendingRedirectPath =
  async (): Promise<LocalStoragePendingRedirectPath> => {
    try {
      const result = await localforage.getItem<Partial<LocalStoragePendingRedirectPath>>(
        LocalStorageKey.PendingRedirectPath
      );

      const pathname = normalizeSegment(result?.pathname);

      if (!pathname) {
        return {
          pathname: null,
          search: null,
          hash: null,
        };
      }

      if (IGNORED_REDIRECT_PATHS.includes(pathname)) {
        return {
          pathname: null,
          search: null,
          hash: null,
        };
      }

      return {
        pathname,
        search: sanitizeSearch(result?.search),
        hash: normalizeSegment(result?.hash),
      };
    } catch (unknownErr) {
      const err = unknownErr as Error;

      logger.warn({
        message: "[_readLocalStoragePendingRedirectPath] Failed.",
        info: {
          err: objectModule.safeErrorAsJson(err),
        },
      });

      return {
        pathname: null,
        search: null,
        hash: null,
      };
    }
  };
